import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Mail, MessageSquare, Check, Sparkles, Reply, Tags, Image as ImageIcon, Loader2, Copy, Trash2, Ban, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const MODELS = [
  { key: "gpt-5.4", label: "GPT-5.4" },
  { key: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
  { key: "gemini-3-flash-preview", label: "Gemini 3 Flash" },
];

export function NoteDialog({ note, onClose, onComplete, onDelete, onBlock, model, setModel, onAi, onDoodle }) {
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const [confirmBlock, setConfirmBlock] = useState(false);

  useEffect(() => { setResult(""); setBusy(""); setConfirmDel(false); setConfirmBlock(false); }, [note?.id]);

  const runAi = async (action) => {
    setBusy(action); setResult("");
    try {
      const text = await onAi(note.id, action, model);
      setResult(text || "Done.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI request failed");
    }
    setBusy("");
  };

  const runDoodle = async () => {
    setBusy("doodle");
    try {
      await onDoodle(note.id, model);
      toast.success("Doodle pinned to your board!");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate a doodle");
    }
    setBusy("");
  };

  const AiBtn = ({ id, icon: Icon, label, onClick }) => (
    <button data-testid={`ai-${id}`} onClick={onClick} disabled={!!busy}
      className="flex items-center gap-1.5 rounded-full bg-white/70 hover:bg-white text-[#2b2620] text-xs font-display font-bold px-3 py-1.5 disabled:opacity-50">
      {busy === id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />} {label}
    </button>
  );

  return (
    <Dialog open={!!note} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="note-dialog" className={`border-none p-0 max-w-md overflow-hidden rounded-[4px] note-${note?.color || "yellow"} grain`}>
        <DialogTitle className="sr-only">{note?.subject || note?.sender_name || "Note"}</DialogTitle>
        {note && (
          <div className="p-7">
            <div className="flex items-center gap-2 mb-3">
              {note.channel === "sms" ? <MessageSquare className="w-4 h-4 text-[#5a5148]" /> : note.channel === "telegram" ? <Send className="w-4 h-4 text-[#5a5148]" /> : <Mail className="w-4 h-4 text-[#5a5148]" />}
              <span className="font-note font-bold text-[#4a4238]">{note.sender_name || note.sender}</span>
            </div>
            {note.subject && <h3 className="font-note font-bold text-2xl text-[#2b2620] mb-2 leading-tight">{note.subject}</h3>}
            <p className="font-note text-lg text-[#2b2620] leading-relaxed whitespace-pre-wrap break-words max-h-[38vh] overflow-auto board-scroll">{note.body}</p>

            <div className="mt-5 border-t border-black/10 pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="tag-label text-[#8c7a55] flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> AI actions</span>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger data-testid="ai-model-select" className="h-7 w-[150px] bg-white/70 border-none text-xs font-display font-bold"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MODELS.map((m) => <SelectItem key={m.key} value={m.key} data-testid={`ai-model-${m.key}`} className="text-xs">{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-2">
                <AiBtn id="summarize" icon={Sparkles} label="Summarize" onClick={() => runAi("summarize")} />
                <AiBtn id="reply" icon={Reply} label="Suggest reply" onClick={() => runAi("reply")} />
                <AiBtn id="categorize" icon={Tags} label="Auto-color" onClick={() => runAi("categorize")} />
                <AiBtn id="doodle" icon={ImageIcon} label="Make doodle" onClick={runDoodle} />
              </div>
              {result && (
                <div data-testid="ai-result" className="mt-3 bg-white/80 rounded-lg p-3 text-sm text-[#2b2620] font-note relative">
                  {result}
                  <button onClick={() => { navigator.clipboard?.writeText(result); toast.success("Copied!"); }} className="absolute top-2 right-2 text-[#8c7a55] hover:text-[#2b2620]"><Copy className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-5">
              {confirmDel ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-note text-[#8c7a55]">Delete forever?</span>
                  <Button data-testid="note-dialog-delete-confirm" onClick={() => { onDelete(note.id); onClose(); }} size="sm" className="rounded-full bg-[#ff3b30] hover:bg-[#e0271d] text-white font-display font-bold h-8">Yes, delete</Button>
                  <button data-testid="note-dialog-delete-cancel" onClick={() => setConfirmDel(false)} className="text-xs font-display font-bold text-[#6b6155] px-1">Cancel</button>
                </div>
              ) : confirmBlock ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-note text-[#8c7a55]">Block this sender?</span>
                  <Button data-testid="note-dialog-block-confirm" onClick={() => { onBlock(note.sender); onClose(); }} size="sm" className="rounded-full bg-[#ff3b30] hover:bg-[#e0271d] text-white font-display font-bold h-8">Block</Button>
                  <button data-testid="note-dialog-block-cancel" onClick={() => setConfirmBlock(false)} className="text-xs font-display font-bold text-[#6b6155] px-1">Cancel</button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <button data-testid="note-dialog-delete" onClick={() => setConfirmDel(true)} className="flex items-center gap-1 text-xs font-display font-bold text-[#a0703c] hover:text-[#ff3b30]">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                  {note.channel !== "manual" && (
                    <button data-testid="note-dialog-block" onClick={() => setConfirmBlock(true)} className="flex items-center gap-1 text-xs font-display font-bold text-[#a0703c] hover:text-[#ff3b30]">
                      <Ban className="w-3.5 h-3.5" /> Block
                    </button>
                  )}
                </div>
              )}
              <Button data-testid="note-dialog-done" onClick={() => { onComplete(note.id); onClose(); }} className="rounded-full bg-[#2b2620] hover:bg-black text-[#fdf6e3] font-display font-bold">
                <Check className="w-4 h-4 mr-1" /> Crumple it done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
